import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress, Tab, Tabs, Alert, Box, Stack, OutlinedInput, Divider, Grid2 as Grid, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import debounce from 'lodash/debounce';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';
import { useErrorBoundary } from 'react-error-boundary';
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import IndependentExpenditureItem from '../../../types/reports/independentExpenditureItem';
import IndependentExpenditureReportContent from '../../../types/reports/miscReportContent';
import ReportListExportSession from './ReportListExportSection';
import IndependentSpendingReportsTable from './IndependentSpendingReportsTable';


const IndependentExpendituresReports = (): JSX.Element => {
  const [expenditures, setExpenditures] = useState<IndependentExpenditureItem[]>();
  const [expenditureSummary, setExpenditureSummary] = useState('No items found.');
  const [isLoading, setIsLoading] = useState(true);

  const [reports, setReports] = useState<IndependentExpenditureReportContent[]>();
  const [ieItemsTableRows, setIeItemsTableRows] = useState<IndependentExpenditureItem[]>();
  const [tableRows, setTableRows] = useState<IndependentExpenditureReportContent[]>();
  const [filersDropdown, setFilersDropdown] = useState([]);
  const [candidatesDropdown, setCandidatesDropdown] = useState([]);
  const [vendorDropdown, setVendorDropdown] = useState([]);
  const [summary, setSummary] = useState('No reports found.');

  const [currentFiler, setCurrentFiler] = useState('All');
  const [currentCandidate, setCurrentCandidate] = useState('All');
  const [currentVendor, setCurrentVendors] = useState('All');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);
  const { showBoundary } = useErrorBoundary();

  const dropDownYears = Array.from(new Array(10), (val, index) => new Date().getFullYear() - index);
  const [reportYear, setReportYear] = useState(new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear());

  const [value, setValue] = React.useState('1');

  const [excelExportUrl, setExcelExportUrl] = useState('');
  const [txtExportUrl, setTxtExportUrl] = useState('');

  //Update & handle change functions

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    setReportYear(new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear());
    setCurrentFiler('All');
    setCurrentCandidate('All');
    setCurrentVendors('All');
    setIsLoading(true);
  };

  const updateReporYear = (event: any) => {
    setReportYear(event.target.value);
  };

  const updateFiler = (event: any) => {
    setCurrentFiler(event.target.value);

  };

  const updateCandidate = (event: any) => {
    setCurrentCandidate(event.target.value);
  }

  const updateVendor = (event: any) => {
    setCurrentVendors(event.target.value);

  }


  //Get functions

  const getFilersDropdown = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/entities/${reportYear}?reportType=IE`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the Independent Expenditure filers.'));
          }
          return result.json();
        })
        .then(result => {
          setFilersDropdown(result);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const getCandidatesDropdown = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/candidates/${reportYear}?reportType=IE`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the Independent Expenditure candidates.'));
          }
          return result.json();
        })
        .then(result => {
          setCandidatesDropdown(result);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const getVendorsDropdown = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/vendors/${reportYear}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get vendors.'));
          }
          return result.json();
        })
        .then(result => {
          setVendorDropdown(result);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const getSummary = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/reports/summary/${reportYear}?reportType=IE`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the Independent Expenditure reports summary.'));
          }
          return result.json();
        })
        .then(result => {
          let count = result.count;
          if (count === 0) {
            setSummary("No reports found.");
            return;
          }
          else {
            setSummary(`Viewing 1 through ${count} of ${count} reports, ${result.totalDisplay}`);
          }


        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const getExpenditureSummary = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/expenditures/summary/${reportYear}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the Independent Expenditures summary.'));
          }
          return result.json();
        })
        .then(result => {
          let count = result.count;
          if (count === 0) {
            setExpenditureSummary("No items found.");
            return;
          }
          else {
            setExpenditureSummary(`Viewing 1 through ${count} of ${count} items, ${result.totalDisplay}`);
          }


        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const getIndependentExpenditureReports = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/reports/${reportYear}?reportTypeDescription=IE&pageSize=1000`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the Independent Expenditure reports.'));
          }
          return result.json();
        })
        .then(result => {
          setTableRows(result);
          setReports(result);
          getSummary();
          setIsLoading(false);
          setExcelExportUrl(`${process.env.REACT_APP_API_BASE_URL}/miscreports/excelOutput/${reportYear}`);
          setTxtExportUrl(`${process.env.REACT_APP_API_BASE_URL}/miscreports/textOutput/${reportYear}`);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const getIndependentExpenditureItems = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/expenditures/${reportYear}?pageSize=1000`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the Independent Expenditures.'));
          }
          return result.json();
        })
        .then(result => {
          const rowsWithIds = result.map((row: any, index: number) => ({
            ...row,
            id: index
          }));
          setIeItemsTableRows(rowsWithIds);
          setExpenditures(rowsWithIds);
          getExpenditureSummary();
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const filterDate = debounce((event: any) => {
    const id = event.target.id;  // Capture values immediately
    const value = event.target.value;

    id === 'startDate' ? setStartDate(value) : setEndDate(value);
  }, 800);

  const handleFilterChange = () => {
    if (value === '1') {
      const filtered = reports?.filter((row: IndependentExpenditureReportContent) => {


        const matchesFiler = currentFiler === 'All' || row.filingEntity.includes(currentFiler);
        const matchesCandidate = currentCandidate === 'All' || row.candidateListing.includes(currentCandidate);


        let matchesStartDate = true;
        let matchesEndDate = true;

        if (startDate.length > 1 && (new Date(row.date) < new Date(startDate))) {
          matchesStartDate = false;
        }

        if (endDate.length > 1 && (new Date(row.date) > new Date(endDate))) {
          matchesEndDate = false;
        }



        return matchesFiler && matchesCandidate && matchesStartDate && matchesEndDate;
      });
      setTableRows(filtered);
      if (filtered) {
        filtered.length > 0 ? setSummary(`Viewing 1 through ${filtered.length} of ${reports?.length} reports`) : setSummary("No reports found.");
      } else {
        setSummary("No reports found.");
      }
    } else {
      const filtered = expenditures?.filter((row: IndependentExpenditureItem) => {


        const matchesFiler = currentFiler === 'All' || row.filingEntity.includes(currentFiler);
        const matchesCandidate = currentCandidate === 'All' || row.candidateListing.includes(currentCandidate);
        const matchesVendor = currentVendor === 'All' || ('vendor' in row && row.vendor.includes(currentVendor));

        return matchesFiler && matchesCandidate && matchesVendor;
      });
      setIeItemsTableRows(filtered);
      if (filtered) {
        filtered.length > 0 ? setExpenditureSummary(`Viewing 1 through ${filtered.length} of ${reports?.length} items`) : setSummary("No items found.");
      } else {
        setExpenditureSummary("No items found.");
      }

    }

  };

  useEffect(() => {
    getIndependentExpenditureReports();
    getIndependentExpenditureItems();
    getFilersDropdown();
    getCandidatesDropdown();
    getVendorsDropdown();
  }, [reportYear, value]);

  useEffect(() => {
    handleFilterChange();
  }, [currentFiler, currentCandidate, startDate, endDate, currentVendor]);


  const filters = (tab: number) => {
    return (<Stack direction={"row"} spacing={1}
      sx={{ mb: 1 }}
    >
      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
        <InputLabel id="year-id">Year</InputLabel>
        <Select
          labelId="year-id"
          id="year-select"
          aria-labelledby='year-id'
          sx={{ fontSize: 'smaller' }}
          label="Report Year"
          value={reportYear}
          onChange={updateReporYear}
        >
          {
            dropDownYears.map((year) =>
              <MenuItem value={year} key={year} >{year}</MenuItem>
            )
          }
        </Select>
      </FormControl>
      {tab === 1 && <>

        <FormControl size="small" sx={{ m: 1, p: 0, maxWidth: 100 }}>
          <OutlinedInput
            aria-label='startDate'
            placeholder={"Start Date"}
            inputRef={startInputRef}
            id="startDate"
            type='text'
            onChange={filterDate}
            sx={{ fontSize: 'smaller' }}
          />
        </FormControl>
        <FormControl size="small" sx={{ m: 1, p: 0, maxWidth: 100 }}>
          <OutlinedInput
            aria-label='endDate'
            placeholder={"End Date"}
            inputRef={endInputRef}
            type='text'
            id="endDate"
            onChange={filterDate}
            sx={{ fontSize: 'smaller' }}
          />

        </FormControl>

      </>

      }

      {tab === 2 &&
        <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
          <InputLabel id="name-id">Name</InputLabel>
          <Select
            labelId="name-id"
            id="name-select"
            aria-labelledby='name-id'
            sx={{ fontSize: 'smaller' }}
            label="Type"
            value={currentVendor}
            onChange={updateVendor}
          >
            <MenuItem value='All' key='All' >All</MenuItem>
            {
              vendorDropdown.map((filer) =>
                <MenuItem value={filer} key={filer} >{filer}</MenuItem>
              )
            }

          </Select>
        </FormControl>}



      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
        <InputLabel id="filer-id">Filer</InputLabel>
        <Select
          labelId="filer-id"
          id="filer-select"
          aria-labelledby='filer-id'
          sx={{ fontSize: 'smaller' }}
          label="Filer"
          value={currentFiler}
          onChange={updateFiler}
        >
          <MenuItem value='All' key='All' >All</MenuItem>
          {
            filersDropdown.map((filer) =>
              <MenuItem value={filer} key={filer} >{filer}</MenuItem>
            )
          }

        </Select>
      </FormControl>
      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
        <InputLabel id="candidate-id">Candidate</InputLabel>
        <Select
          labelId="candidate-id"
          id="candidate-select"
          aria-labelledby='candidate-id'
          sx={{ fontSize: 'smaller' }}
          label="Candidate"
          value={currentCandidate}
          onChange={updateCandidate}
        >
          <MenuItem value='All' key='All' >All</MenuItem>
          {
            candidatesDropdown.map((candidate) =>
              <MenuItem value={candidate} key={candidate} >{candidate}</MenuItem>
            )
          }
        </Select>
      </FormControl>
    </Stack>);
  };


  return (
    <Main>
      <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
          variant="h5"
          align={'left'}
          data-aos={'fade-up'}
          gutterBottom
          sx={{
            fontWeight: 700,
          }}
          paddingTop={3}
          marginBottom={2}
        >
          Independent Expenditure Reports
        </Typography>
        <Divider />
        <Grid container marginBottom={2} marginTop={3}>
          <Grid size={12}>
            <Typography marginBottom={1} sx={{ textIndent: 10 }} component={'p'} variant='body1'>The Massachusetts campaign finance law requires the disclosure of expenditures of more than $250 in support of or in opposition to candidates that are made independent of any political committee. There is no statutory limit on such expenditures. </Typography>
            <Typography marginBottom={1} sx={{ textIndent: 10 }} component={'p'} variant='body1'>Independent expenditure reports filed before January 1, 2006 are available for viewing in PDF format in the OCPF Archive. Independent expenditure reports filed on or after January 1, 2006 are displayed on this page in chronological order. The reports are grouped by year so you must first make a selection from the dropdown list to view reports other than the current year's filed reports. Within each year, the reports can be filtered by a date range, the individual or entity making the expenditure, the subject (candidate) of the expenditure, or the position (support or oppose) taken by the filer.</Typography>
          </Grid>
          <Grid size={12}>
            <TabContext value={value}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={handleChange} aria-label="lab API tabs example">
                  <Tab label="Reports" value="1" />
                  <Tab label="Items" value="2" />
                </TabList>
              </Box>
              <TabPanel value="1">
                <Grid container marginBottom={2} marginTop={3}>
                  <Grid size={{ xs: 12, md: 10 }}>
                    {filters(1)}
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <ReportListExportSession excelExportUrl={excelExportUrl} txtExportUrl={txtExportUrl}></ReportListExportSession>
                    </Grid>
                  <Grid size={12}>
                    <Alert icon={false} severity="info"
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        '.MuiAlert-message': {
                          padding: '4px',
                        }
                      }}
                    >
                      {summary}
                    </Alert>
                  </Grid>
                </Grid>
                <IndependentSpendingReportsTable tableRows={tableRows} type='ieReports' />
              </TabPanel>
              <TabPanel value="2">
                <Grid container marginBottom={2} marginTop={3}>
                  <Grid size={{ xs: 12, md: 10 }}>
                    {filters(2)}
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    {/* <ReportListExportSession></ReportListExportSession> */}
                    </Grid>
                    <Grid size={12}>
                      <Alert icon={false} severity="info"
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          '.MuiAlert-message': {
                            padding: '4px',
                          }
                        }}
                      >
                        {expenditureSummary}
                      </Alert>
                    </Grid>
                  </Grid>

                  <IndependentSpendingReportsTable tableRows={ieItemsTableRows} type='ieItems' />

              </TabPanel>
            </TabContext>
          </Grid>



        </Grid>

       {/*loading indicator*/}
             <CircularProgress
                  aria-label='loading-indicator'
                  size={50}
                  className={isLoading? "": "hidden"}
                  sx={{
                    position: "fixed",
                    left: "50%",
                    top: "20%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 2
                   }}
              />

      </Container>
    </Main>
  );
};

export default IndependentExpendituresReports;
